import { TranscriptionService } from '@klangio/ai2notes-ts-client';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PlayDisabledIcon from '@mui/icons-material/PlayDisabled';
import { CircularProgress, Fab, Tooltip } from '@mui/material';
import {
  useContext, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { AppConstantsContext, TranscriptionStoreContext } from '../../../../context';
import { JobType } from '../../../../entities';
import { backendService } from '../../../../services';

const useStyles = makeStyles()(() => ({
  playButton: {
    gridArea: 'play',
  },
  hidden: {
    display: 'none',
  },
}));

const SimpleAudioPlayer = () => {
  const { classes } = useStyles();
  const audioRef = useRef<HTMLAudioElement>(null);
  const { jobType } = useContext(AppConstantsContext);
  const [isPlaying, setIsPlaying] = useState(false);
  const { selectedTranscription } = useContext(TranscriptionStoreContext);
  const [audio, setAudio] = useState<string>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (jobType === JobType.TranscribeScan) {
      setAudio(null);
    } else if (selectedTranscription.source !== 'youtube') {
      setAudio(null);
    } else {
      setAudio('youtube');
    }
  }, []);

  const togglePlayback = async () => {
    if (!audio) {
      setIsLoading(true);
      backendService.setAxiosResponseTypeToBlob();
      TranscriptionService.transcriptionMp3(selectedTranscription.id)
        .then((audioData: Blob) => {
          const audioUrl = URL.createObjectURL(new Blob([audioData], { type: 'audio/mpeg' }));
          setAudio(audioUrl);
          audioRef.current.play();
          audioRef.current.volume = 0.5;
          audioRef.current.currentTime = 0;
          setIsPlaying(true);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
          setError(true);
        });
      backendService.clearAxiosConfig();
      return;
    }
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying((prev) => !prev);
  };

  useEffect(() => {
    if (isPlaying) {
      audioRef.current.play();
    }
  }, [audio]);

  if (error) {
    return (
      <Tooltip title={t('home.songbook.no_sound_available')}>
        <div>
          <Fab disabled>
            <PlayDisabledIcon fontSize="large" />
          </Fab>
        </div>
      </Tooltip>
    );
  }

  return (
    <div>
      <audio ref={audioRef} src={audio} preload="auto" className={classes.hidden} />
      <Fab
        className={classes.playButton}
        id={isPlaying ? 'btn_songbook_pause' : 'btn_songbook_play'}
        onClick={togglePlayback}
        color={isLoading ? 'secondary' : 'primary'}
        disabled={error}
        size="large"
      >
        {isLoading && <CircularProgress size="100%" />}
        {!isLoading
          && (isPlaying ? <PauseIcon fontSize="large" /> : <PlayArrowIcon fontSize="large" />)}
      </Fab>
    </div>
  );
};

export default SimpleAudioPlayer;
